import React from 'react';
import {
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material';
import { STATE_FULL_NAME } from 'utils/constants';
import AddressSearch from './AddressSearch';
import { COUNTRIES, getCountryName } from './countries';
import { formatAddressDisplay } from './formatAddressDisplay';
import { mapPlaceToFormValues } from './mapPlaceToFormValues';

const AddressFormFields = ({
  values,
  onChange,
  errors = {},
  searchLabel = 'Search address',
  showSearch = true,
  searchPopupZIndex,
  fieldSize = 'medium',
  fieldMargin = 'dense',
}) => {
  const countryCode = values.countryCode || '';
  const isUnitedStates = countryCode === 'US';

  const handleFieldChange = (field) => (event) => {
    onChange({ [field]: event.target.value });
  };

  const handleCountryChange = (event) => {
    const nextCode = event.target.value;
    onChange({
      countryCode: nextCode,
      countryName: nextCode ? getCountryName(nextCode) : '',
      state: nextCode === 'US' ? values.state : values.state,
    });
  };

  const handlePlaceSelect = (place) => {
    onChange(mapPlaceToFormValues(place));
  };

  const hasAddressContent = Boolean(
    values.street || values.addressLine2 || values.city || values.state || values.zipCode
  );
  const searchDisplayValue = hasAddressContent
    ? formatAddressDisplay(values, { includeCountry: true })
    : '';

  return (
    <>
      {showSearch && (
        <FormControl fullWidth variant="outlined" error={Boolean(errors.street)} sx={{ mb: 1 }}>
          <AddressSearch
            onSelect={handlePlaceSelect}
            label={searchLabel}
            size={fieldSize}
            margin={fieldMargin}
            value={searchDisplayValue}
            autoComplete="new-password"
            popupZIndex={searchPopupZIndex}
          />
          {errors.street && <FormHelperText>{errors.street}</FormHelperText>}
        </FormControl>
      )}

      <TextField
        fullWidth
        label="Street address"
        margin={fieldMargin}
        variant="outlined"
        value={values.street || ''}
        onChange={handleFieldChange('street')}
        error={Boolean(errors.street)}
        helperText={errors.street}
      />

      <TextField
        fullWidth
        label="Suite, apt, or building (optional)"
        margin={fieldMargin}
        variant="outlined"
        value={values.addressLine2 || ''}
        onChange={handleFieldChange('addressLine2')}
        error={Boolean(errors.addressLine2)}
        helperText={errors.addressLine2}
      />

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="City"
            margin={fieldMargin}
            variant="outlined"
            value={values.city || ''}
            onChange={handleFieldChange('city')}
            error={Boolean(errors.city)}
            helperText={errors.city}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            select
            label="Country"
            margin={fieldMargin}
            variant="outlined"
            value={countryCode}
            onChange={handleCountryChange}
            error={Boolean(errors.countryCode)}
            helperText={errors.countryCode}
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                if (!selected) {
                  return '';
                }
                return getCountryName(selected);
              },
            }}
          >
            {COUNTRIES.map((country) => (
              <MenuItem key={country.code} value={country.code}>
                {country.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={isUnitedStates ? 4 : 6}>
          {isUnitedStates ? (
            <TextField
              fullWidth
              select
              label="State"
              margin={fieldMargin}
              variant="outlined"
              value={values.state || ''}
              onChange={handleFieldChange('state')}
              error={Boolean(errors.state)}
              helperText={errors.state}
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => {
                  if (!selected) {
                    return '';
                  }
                  return STATE_FULL_NAME[selected] || selected;
                },
              }}
            >
              {Object.entries(STATE_FULL_NAME).map(([abbr, name]) => (
                <MenuItem key={abbr} value={abbr}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              fullWidth
              label="State / province / region"
              margin={fieldMargin}
              variant="outlined"
              value={values.state || ''}
              onChange={handleFieldChange('state')}
              error={Boolean(errors.state)}
              helperText={errors.state}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={isUnitedStates ? 8 : 6}>
          <TextField
            fullWidth
            label="ZIP / postal code"
            margin={fieldMargin}
            variant="outlined"
            value={values.zipCode || ''}
            onChange={handleFieldChange('zipCode')}
            error={Boolean(errors.zipCode)}
            helperText={errors.zipCode}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default AddressFormFields;
